import { useMemo } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { roundPotentiallyInfiniteNumber } from "@features/mmm/utils/formatters";

enum MetricBoxContainerColor {
  WHITE,
  GREEN,
  RED,
  GREY,
  PRIMARY,
  LIGHT_PRIMARY,
}

export enum MetricBoxDirectionArrow {
  UP = "up",
  DOWN = "down",
  SIDEWAYS = "sideways",
}

export type GetMetricColorTypes = {
  isNeutral?: boolean;
  inverseColor: boolean;
  color?: MetricBoxContainerColor;
};

export const getMetricColor = (options?: GetMetricColorTypes) => {
  const { isNeutral, inverseColor, color } = options ?? {};
  if (isNeutral) {
    return { "text-v2-action bg-v2-highlight-information": isNeutral };
  }

  return inverseColor
    ? {
        "bg-gray-3 text-interstellar-1": color === MetricBoxContainerColor.GREY,
        "bg-alien text-white": color === MetricBoxContainerColor.GREEN,
        "bg-mars text-white": color === MetricBoxContainerColor.RED,
        "text-interstellar-1": color === MetricBoxContainerColor.WHITE,
        "bg-primary text-white": color === MetricBoxContainerColor.PRIMARY,
        "bg-v2-secondary text-interstellar":
          color === MetricBoxContainerColor.LIGHT_PRIMARY,
      }
    : {
        "bg-gray-3 text-interstellar-1": color === MetricBoxContainerColor.GREY,
        "bg-v2-highlight-notice text-v2-action-positive":
          color === MetricBoxContainerColor.GREEN,
        "bg-v2-highlight-alert text-v2-action-negative":
          color === MetricBoxContainerColor.RED,
        "text-interstellar-1": color === MetricBoxContainerColor.WHITE,
        "bg-v2-secondary text-interstellar":
          color === MetricBoxContainerColor.LIGHT_PRIMARY,
      };
};

export const formatPercentage = (percentage: number) => {
  return `${roundPotentiallyInfiniteNumber(Math.abs(percentage), 0)}%`;
};

export function useMetricComparisonDate({
  compareData,
}: {
  compareData: {
    metricChangePercentage: number;
    isDesiredDirection: boolean;
  };
}) {
  const [text, arrowType, color] = useMemo(() => {
    if (Math.round(compareData.metricChangePercentage) === 0) {
      return ["-%", null, MetricBoxContainerColor.GREY];
    } else if (compareData.metricChangePercentage > 0) {
      return [
        formatPercentage(compareData.metricChangePercentage),
        MetricBoxDirectionArrow.UP,
        compareData.isDesiredDirection
          ? MetricBoxContainerColor.GREEN
          : MetricBoxContainerColor.RED,
      ];
    } else {
      return [
        formatPercentage(compareData.metricChangePercentage),
        MetricBoxDirectionArrow.DOWN,
        compareData.isDesiredDirection
          ? MetricBoxContainerColor.GREEN
          : MetricBoxContainerColor.RED,
      ];
    }
  }, [compareData]);

  return { text, arrowType, color };
}

export function PercentageChangeChip({
  compareData,
  adopted,
  className,
  isNeutral,
  isHistorical = false,
}: {
  compareData: {
    metricChangePercentage: number;
    isDesiredDirection: boolean;
  };
  adopted?: boolean;
  className?: string;
  isNeutral?: boolean;
  isHistorical?: boolean;
}) {
  const { text, arrowType, color } = useMetricComparisonDate({ compareData });

  const inverseColor = !!adopted;

  return (
    <div
      className={twMerge(
        clsx(
          "h-4 w-fit font-semibold flex justify-between items-center gap-0.5 py-1 px-1.5 rounded-full text-xs",
          getMetricColor({
            inverseColor,
            color,
            isNeutral,
          }),
          { "min-w-[54px]": !isHistorical, "bg-transparent": isHistorical },
          className,
        ),
      )}
    >
      {!isHistorical && (
        <>
          {arrowType ? (
            <i
              className={clsx("fa-regular text-3xs", {
                "fa-arrow-up": arrowType === MetricBoxDirectionArrow.UP,
                "fa-arrow-down": arrowType === MetricBoxDirectionArrow.DOWN,
              })}
            ></i>
          ) : (
            // placeholder
            <span></span>
          )}
          {text}
        </>
      )}
      <i className="fa-regular fa-circle-info text-3xs" />
    </div>
  );
}
